import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { TabContext, TabPanel } from '@mui/lab';
import { Grid } from '@mui/material';
import useImages from 'api/useImages';
import useOffers from 'api/useOffers';
import useOffersUI from 'api/useOffersUI';
import { AxiosError } from 'axios';
import { Offer, OfferUI, RollingOfferProductSequence } from 'common/contracts';
import {
  MIN_ROLLING_SUB_OFFERS,
  MIN_USD_PRICE,
  PRICE_VALUE_LIMIT,
  TRIMMED_INPUT_REGEX
} from 'constants/constants';
import { ERollingOfferFormState, OfferType } from 'constants/enums';
import Decimal from 'decimal.js';
import { useFormik } from 'formik';
import { usePageTypeByFeatureFlag } from 'hooks/usePageTypeByFF';
import { errorResponse } from 'utils/errorsTextHelper';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import * as yup from 'yup';

import AcContentWrapper from 'components/AcContentWrapper/AcContentWrapper';
import AcViewWrapper from 'components/AcViewWrapper/AcViewWrapper';

import { EToasterStatus, showToast } from '../../../utils/showToast';
import { RollingOfferFormProps } from '../types';

import RollingOfferHeader from './Sections/Header';
import RollingOfferMock from './Sections/Mock';
import RollingOfferModals from './Sections/Modals';
import ConfigurationTab from './Tabs/ConfigurationTab';
import ScheduleTab from './Tabs/ScheduleTab';
import SettingsTab from './Tabs/SettingsTab';

const DEFAULT_OFFER_DESIGN_EXTERNAL_ID = 'Default_RollingOffer';

const RollingOfferForm: React.FC<RollingOfferFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const { rollingOfferId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState<Partial<Offer>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [tab, setTab] = useState(ERollingOfferFormState.GENERAL);
  const [currentOfferUI, setCurrentOfferUI] = useState('');
  const { getOffersUI } = useOffersUI(publisherId, currentOfferUI);
  const [chosenSegment, setChosenSegment] = useState<string[]>([]);

  const { addOffer, updateOffer, getOffer, getOffers } = useOffers({
    offerId: rollingOfferId,
    offerType: OfferType.ROLLING_OFFER
  });
  const { getImages } = useImages(publisherId);
  const [productsSequence, setProductsSequence] = useState<
    RollingOfferProductSequence[]
  >([]);

  const { isPageDisabled } = usePageTypeByFeatureFlag('Rolling');

  const checkUniqueness = (value: string | undefined, field: keyof Offer) => {
    const isTaken = getOffers.data?.offers.find((o: Offer) => {
      if (!edit) return o[field] === value;
      return o[field] === value && data[field] !== value;
    });
    return !isTaken;
  };

  const offerSchema = yup.object().shape({
    name: yup
      .string()
      .transform((value) => value.trim())
      .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
      .required('Name is required')
      .test('uniqueness', 'Name already exists', (value) => {
        return checkUniqueness(value, 'name');
      }),
    offerUiId: yup.string().required('Offer Design is required'),
    publisherOfferId: yup
      .string()
      .transform((value) => value.trim())
      .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
      .required('External ID is required')
      .test('uniqueness', 'External ID already exists', (value) => {
        return checkUniqueness(value, 'publisherOfferId');
      }),
    setAsFree: yup.boolean(),
    price: yup.number().when('setAsFree', {
      is: true,
      then: yup.number(),
      otherwise: yup
        .number()
        .required('price is required')
        .min(MIN_USD_PRICE, 'Price must be at least 0.8')
        .max(5000, 'Price must be at most 5000')
        .test('maxDigits', 'Price must have at most 10 digits', (value) =>
          value
            ? value.toString().replace('.', '').length <= PRICE_VALUE_LIMIT
            : true
        )
        .test(
          'maxDecimalPlaces',
          'Price must have at most 2 decimal points',
          (value) => {
            if (!value) return true;
            const decimalPart = value.toString().split('.')[1];
            return !decimalPart || decimalPart.length <= 2;
          }
        )
        .test(
          'maxIntegerPart',
          'Price integer part must have at most 8 digits',
          (value) => {
            if (!value) return true;
            const integerPart = value.toString().split('.')[0];
            return integerPart.length <= 8;
          }
        )
    }),
    priority: yup
      .number()
      .min(1, 'Priority must be at least 1')
      .max(10, 'Priority must be at most 10')
      .required('Priority is required'),
    priceDiscount: yup
      .number()
      .min(0, 'Price discount cannot be negative')
      .max(99, 'Price discount cannot be more than 99%'),
    productSale: yup.number().min(0, 'Sale amount cannot be negative')
  });

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  } = useFormik({
    validateOnMount: true,
    validationSchema: offerSchema,
    enableReinitialize: true,
    initialValues: {
      active: edit ? data?.active : true,
      publisherOfferId: data.publisherOfferId || '',
      name: data.name ? `${data.name}${dup ? '_copy' : ''}` : '',
      type: OfferType.ROLLING_OFFER,
      priority: data.priority || 1,
      offerUiId: (edit || dup
        ? edit
          ? data.offerUi?._id || data.offerUi?.offerUiId || ''
          : (getOffer?.data?.result?.offerUi?.offerUiId as string) || ''
        : (getOffersUI?.data?.result?.find(
            (oi: OfferUI) => oi.externalId === DEFAULT_OFFER_DESIGN_EXTERNAL_ID
          )?._id as string) || ''
      ).toString(),
      segments: data.segments || [],
      schedule: data.schedule || {
        permanent: false,
        timeFrames: []
      },
      price: MIN_USD_PRICE,
      productsSequence:
        (data.productsSequence &&
          data.productsSequence.forEach((product: any) => ({
            ...product,
            priceInUsdCents:
              product?.priceInUsdCents === 0
                ? 0
                : new Decimal(product?.priceInUsdCents).div(100).toNumber()
          }))) ||
        []
    },
    onSubmit: async (values) => {
      const newOffer: Partial<Offer> & { sectionId?: string | null } = {
        active: values.active,
        publisherOfferId: values.publisherOfferId,
        name: values.name,
        type: OfferType.ROLLING_OFFER,
        priority: values.priority,
        offerUiId: values.offerUiId,
        segments: chosenSegment,
        schedule: {
          ...values.schedule,
          timeFrames: values?.schedule?.permanent
            ? []
            : values?.schedule.timeFrames
        },
        productsSequence: productsSequence.length
          ? productsSequence.map((seq) => {
              const hasValidProducts = seq.products.some(
                (product) => product?.product?.productId
              );
              const products = hasValidProducts
                ? seq.products.map((product) => ({
                    productId: product?.product?.productId,
                    quantity: product.quantity,
                    publisherProductId: product.product?.publisherProductId
                  }))
                : [];
              return {
                index: seq.index || 1,
                products,
                priceInUsdCents:
                  seq?.priceInUsdCents === 0
                    ? 0
                    : new Decimal(seq?.priceInUsdCents).mul(100).toNumber(),
                ...(seq?.priceDiscount?.discount !== 0 && {
                  priceDiscount: seq?.priceDiscount
                }),
                ...(seq?.productSale?.sale !== 0 && {
                  productSale: seq?.productSale
                }),
                ...(seq?.badges?.length && {
                  badges: [
                    {
                      badgeId: seq.badges[0]?.badge?.badgeId,
                      publisherBadgeId: seq.badges[0]?.badge?.publisherBadgeId
                    }
                  ]
                })
              };
            })
          : []
      };
      if (edit && rollingOfferId && !dup) {
        updateOffer.mutate(
          { offerId: rollingOfferId, form: newOffer },
          {
            onSuccess: () => {
              showToast({
                message: 'Rolling Offer edited successfully',
                status: EToasterStatus.SUCCESS
              });
              navigate('../');
            },
            onError: (error) => {
              if (error instanceof AxiosError) {
                showToast({
                  message: errorResponse(error),
                  status: EToasterStatus.ERROR
                });
              }
            }
          }
        );
      } else {
        addOffer.mutate(newOffer, {
          onSuccess: () => {
            showToast({
              message: 'New Rolling Offer added successfully',
              status: EToasterStatus.SUCCESS
            });
            navigate('../');
          },
          onError: (error) => {
            if (error instanceof AxiosError) {
              showToast({
                message: errorResponse(error),
                status: EToasterStatus.ERROR
              });
            }
          }
        });
      }
    }
  });

  useEffect(() => {
    if (!!rollingOfferId && !getOffer.isLoading && getOffer.data?.result) {
      setData(getOffer.data.result);
    }
  }, [rollingOfferId, getOffer?.data, getOffer?.isLoading]);

  useEffect(() => {
    if (!data) return;
    setIsLoading(false);
    setCurrentOfferUI(data.offerUi?._id || '');
    if (
      data.productsSequence &&
      (data.productsSequence as RollingOfferProductSequence[])
    ) {
      setProductsSequence(
        (data.productsSequence as RollingOfferProductSequence[]).map(
          (item) => ({
            ...item,
            priceInUsdCents:
              item?.priceInUsdCents === 0
                ? 0
                : new Decimal(item?.priceInUsdCents).div(100).toNumber()
          })
        ) || []
      );
    }
  }, [data]);

  useEffect(() => {
    if (!productsSequence?.length) {
      const initialProductsSequence = Array.from(
        { length: MIN_ROLLING_SUB_OFFERS },
        (_, i) => ({
          index: i + 1,
          priceInUsdCents: 0,
          products: [
            {
              product: {
                images: []
              }
            }
          ]
        })
      );
      setProductsSequence(initialProductsSequence);
    }
  }, [productsSequence, setProductsSequence]);

  const hasMissingProductDetails = useMemo(() => {
    return productsSequence.some(
      ({ products }) =>
        !products.length ||
        products.some(
          ({ product, quantity }) => !product?.publisherProductId || !quantity
        )
    );
  }, [productsSequence]);

  useEffect(() => {
    setCurrentOfferUI(values.offerUiId);
  }, [values.offerUiId]);

  const offerDesignItems = useMemo(
    () =>
      getOffersUI.data?.result
        .filter(
          (oi: OfferUI) => oi.offerUiType === OfferDesignTypes.RollingOffer
        )
        .map((offerUI: OfferUI) => ({
          content: offerUI.name,
          key: offerUI._id,
          value: offerUI._id
        })) || [],
    [getOffersUI?.data?.result]
  );

  const defaultOfferDesignId = useMemo(
    () =>
      getOffersUI?.data?.result
        ? getOffersUI?.data?.result.find(
            (oi: OfferUI) => oi.externalId === DEFAULT_OFFER_DESIGN_EXTERNAL_ID
          )?._id
        : '',
    [getOffersUI?.data?.result]
  );

  useEffect(() => {
    if (!rollingOfferId) {
      setCurrentOfferUI(defaultOfferDesignId);
    }
  }, [defaultOfferDesignId, rollingOfferId]);

  const formikProps = {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  };

  return (
    <>
      <AcViewWrapper
        isDisabled={isPageDisabled}
        header={
          <RollingOfferHeader
            edit={edit}
            formikProps={formikProps}
            rollingOfferId={rollingOfferId}
            tab={tab}
            setTab={setTab}
            setIsUpdateDialogOpen={setIsUpdateDialogOpen}
            setIsSaveDialogOpen={setIsSaveDialogOpen}
            productsSequence={productsSequence}
            setProductsSequence={setProductsSequence}
            isLoading={isLoading}
            hasMissingProductDetails={hasMissingProductDetails}
          />
        }
      >
        {!getImages.isLoading && getImages.data && (
          <AcContentWrapper className="formContent">
            <Grid
              container
              justifyContent={'space-between'}
              position={'relative'}
            >
              <Grid item xs={tab === ERollingOfferFormState.SCHEDULE ? 12 : 6}>
                <TabContext value={tab}>
                  <TabPanel
                    value={ERollingOfferFormState.GENERAL}
                    sx={{ padding: 0 }}
                  >
                    <SettingsTab
                      formikProps={formikProps}
                      rollingOfferId={rollingOfferId}
                      edit={edit}
                      offerDesignItems={offerDesignItems}
                      chosenSegment={chosenSegment}
                      setChosenSegment={setChosenSegment}
                      data={data}
                      productsSequence={productsSequence}
                      setProductsSequence={setProductsSequence}
                      dup={dup}
                    />
                  </TabPanel>
                  <TabPanel
                    value={ERollingOfferFormState.CONFIGURATION}
                    sx={{ padding: 0 }}
                  >
                    <ConfigurationTab
                      formikProps={formikProps}
                      rollingOfferId={rollingOfferId}
                      productsSequence={productsSequence}
                      setProductsSequence={setProductsSequence}
                    />
                  </TabPanel>
                  <TabPanel
                    value={ERollingOfferFormState.SCHEDULE}
                    sx={{ padding: 0 }}
                  >
                    <ScheduleTab
                      formikProps={formikProps}
                      rollingOfferId={rollingOfferId}
                      edit={edit}
                    />
                  </TabPanel>
                </TabContext>
              </Grid>
              {tab !== ERollingOfferFormState.SCHEDULE && (
                <RollingOfferMock
                  currentOfferUI={currentOfferUI}
                  productsSequence={productsSequence}
                />
              )}
            </Grid>
          </AcContentWrapper>
        )}
      </AcViewWrapper>
      <RollingOfferModals
        isSaveDialogOpen={isSaveDialogOpen}
        isUpdateDialogOpen={isUpdateDialogOpen}
        setIsSaveDialogOpen={setIsSaveDialogOpen}
        setIsUpdateDialogOpen={setIsUpdateDialogOpen}
        submitForm={submitForm}
      />
    </>
  );
};

export default RollingOfferForm;
